import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import {
  ChevronDown,
  ChevronUp,
  UserCheck,
  CheckCircle,
  Truck,
  PackageCheck,
} from "lucide-react";
import { OrderListSkeleton } from "../components/Skeleton";
import "./OrdersinProgress.css";

const OrdersInProgress = () => {
  const { buyer } = useBuyer();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSeller, setExpandedSeller] = useState(null);

  // ─────────────────────────────────────────────────────────────
  // Utility to safely parse numbers
  // ─────────────────────────────────────────────────────────────
  const num = useCallback((val) => {
    if (val === null || val === undefined || val === "") return 0;

    const parsed = parseFloat(val);

    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Configuration
  // ─────────────────────────────────────────────────────────────
  const config = useMemo(
    () => ({
      statuses: [
        "Accepted",
        "Assigned",
        "Assortment",
        "Assorted",
        "FinalCorrection",
        "RTD",
        "PickedUp",
        "InWarehouse",
        "Dispatched",
      ],
      title: "Orders in Progress",
    }),
    [],
  );

  // ─────────────────────────────────────────────────────────────
  // Order metrics calculator
  // ─────────────────────────────────────────────────────────────
  const enrichOrderWithMetrics = useCallback(
    (order) => {
      const orderDetailEntries = Object.values(order.orderDetail || {});
      const hasDetail = orderDetailEntries.length > 0;

      // ─────────────────────────────────────────────────────────
      // 1. Wastage Calculation
      // ─────────────────────────────────────────────────────────
      const baseWastage =
        num(order.wastage) +
        num(order.wastageOperator) +
        num(order.wastageJmi) +
        num(order.specificationMC);

      const hasSpecification =
        order.specification &&
        order.specification.toString().trim().toUpperCase() !== "PLANE";

      const specAdditions = hasSpecification
        ? (num(order.specificationMC) * num(order.specificationMakingJmi)) /
            100 +
          (num(order.specificationMC) *
            num(order.specificationMakingOperator)) /
            100
        : 0;

      const totalWastage =
        num(order.buyerDeskCalculation) + baseWastage + specAdditions;

      // ─────────────────────────────────────────────────────────
      // 2. Making Charges
      // ─────────────────────────────────────────────────────────
      const setMcBase = num(order.setMc || order.lotDetails?.[0]?.setMc);

      const setMc =
        setMcBase +
        setMcBase *
          ((num(order.setMakingJmi) + num(order.setMakingOperator)) / 100);

      const gramMcBase = num(
        order.netGramMc || order.lotDetails?.[0]?.netGramMc,
      );

      const gramMcPerGram =
        gramMcBase +
        gramMcBase *
          ((num(order.gramMakingJmi) + num(order.gramMakingOperator)) / 100);

      const specMcBase = num(
        order.specificationGramRate ||
          order.lotDetails?.[0]?.specificationGramRate,
      );

      const specMcPerGram =
        specMcBase +
        specMcBase *
          ((num(order.specificationGramRateJmi) +
            num(order.specificationGramRateOperator)) /
            100);

      // ─────────────────────────────────────────────────────────
      // 3. Weight & Quantity Calculation
      // ─────────────────────────────────────────────────────────
      let displayGrossWt = 0;
      let displayNetWt = 0;
      let displaySpecWt = 0;
      let displayQty = 1;

      // This is the final calculated stamp/certificate amount
      let displayStampCost = 0;

      if (hasDetail) {
        // ───────────────────────────────────────────────────────
        // Final quantity
        // ───────────────────────────────────────────────────────
        const totalFinalQty = orderDetailEntries.reduce((sum, d) => {
          const fQty =
            d.finalQty !== undefined
              ? num(d.finalQty)
              : num(d.qty) - num(d.returnQty);

          return sum + Math.max(0, fQty);
        }, 0);

        // ───────────────────────────────────────────────────────
        // Final Gross
        // ───────────────────────────────────────────────────────
        const totalDetailFinalGross = orderDetailEntries.reduce((sum, d) => {
          const fGross =
            d.finalGrossWt !== undefined
              ? num(d.finalGrossWt)
              : num(d.qty) * num(d.grossWT) - num(d.returnGrossWt);

          return sum + Math.max(0, fGross);
        }, 0);

        // ───────────────────────────────────────────────────────
        // Final Net
        // ───────────────────────────────────────────────────────
        const totalDetailFinalNet = orderDetailEntries.reduce((sum, d) => {
          const fNet =
            d.finalNetWt !== undefined
              ? num(d.finalNetWt)
              : num(d.qty) * num(d.netwt) - num(d.returnNetWt);

          return sum + Math.max(0, fNet);
        }, 0);

        // ───────────────────────────────────────────────────────
        // Final Specification Weight
        // ───────────────────────────────────────────────────────
        const totalDetailFinalSpec = orderDetailEntries.reduce((sum, d) => {
          const fSpec =
            d.finalSpecWt !== undefined
              ? num(d.finalSpecWt)
              : num(d.qty) * num(d.specWT) - num(d.returnSpecWt);

          return sum + Math.max(0, fSpec);
        }, 0);

        // ───────────────────────────────────────────────────────
        // Stamp & Certificate Cost
        //
        // IMPORTANT:
        // stampCertificateQtyPerSet is inside each orderDetail.
        //
        // Example:
        // qty = 1
        // stampCertificateQtyPerSet = 2
        // stampCertificateCost = 50
        //
        // 1 × 2 × 50 = ₹100
        // ───────────────────────────────────────────────────────
        displayStampCost = orderDetailEntries.reduce((sum, d) => {
          const qty =
            d.finalQty !== undefined
              ? num(d.finalQty)
              : num(d.qty) - num(d.returnQty);

          const stampQtyPerSet = num(d.stampCertificateQtyPerSet);

          const stampCost = num(order.stampCertificateCost);

          return sum + stampQtyPerSet * Math.max(0, qty) * stampCost;
        }, 0);

        displayQty =
          totalFinalQty > 0 ? totalFinalQty : num(order.quantity) || 1;

        displayGrossWt = parseFloat(totalDetailFinalGross.toFixed(3));

        displayNetWt = parseFloat(totalDetailFinalNet.toFixed(3));

        displaySpecWt = parseFloat(totalDetailFinalSpec.toFixed(3));
      } else {
        // ───────────────────────────────────────────────────────
        // No orderDetail
        // ───────────────────────────────────────────────────────
        let lotNet = 0;
        let lotGross = 0;
        let lotSpec = 0;
        let totalSet = 0;

        // ───────────────────────────────────────────────────────
        // Selected Variants
        // ───────────────────────────────────────────────────────
        if (
          Array.isArray(order.selectedVariants) &&
          order.selectedVariants.length > 0
        ) {
          order.selectedVariants.forEach((variant) => {
            const lot = (order.lotDetails || []).find(
              (l) => l.size === variant.size,
            );

            const qty = num(variant?.quantity) || 1;

            const avgNet = lot
              ? num(lot.avgNetWt)
              : num(order.netWt) / (num(order.quantity) || 1);

            const avgGross = lot
              ? num(lot.avgGrossWt)
              : num(order.grossWt) / (num(order.quantity) || 1);

            const avgSpec = lot
              ? num(lot.avgSpecWt)
              : num(order.specificationWt) / (num(order.quantity) || 1);

            totalSet += qty;

            lotNet += avgNet * qty;
            lotGross += avgGross * qty;
            lotSpec += avgSpec * qty;

            // Stamp & Certificate from selected variant
            const stampQtyPerSet = num(variant.stampCertificateQtyPerSet);

            displayStampCost +=
              stampQtyPerSet * qty * num(order.stampCertificateCost);
          });
        }

        // ───────────────────────────────────────────────────────
        // Lot Details
        // ───────────────────────────────────────────────────────
        else if (
          Array.isArray(order.lotDetails) &&
          order.lotDetails.length > 0
        ) {
          order.lotDetails.forEach((lot) => {
            const qty =
              num(lot.quantity) || num(lot.set) || num(order.quantity) || 1;

            totalSet += qty;

            lotNet += num(lot.avgNetWt || num(lot.netWt) / qty) * qty;

            lotGross += num(lot.avgGrossWt || num(lot.grossWt) / qty) * qty;

            lotSpec += num(lot.avgSpecWt || num(lot.specWt) / qty) * qty;

            // If stamp quantity exists in lotDetails
            const stampQtyPerSet = num(lot.stampCertificateQtyPerSet);

            displayStampCost +=
              stampQtyPerSet * qty * num(order.stampCertificateCost);
          });
        }

        displayQty = totalSet > 0 ? totalSet : num(order.quantity) || 1;

        displayGrossWt = parseFloat(
          (lotGross > 0 ? lotGross : num(order.grossWt)).toFixed(3),
        );

        displayNetWt = parseFloat(
          (lotNet > 0 ? lotNet : num(order.netWt)).toFixed(3),
        );

        displaySpecWt = parseFloat(
          (lotSpec > 0 ? lotSpec : num(order.specificationWt)).toFixed(3),
        );

        // ───────────────────────────────────────────────────────
        // Fallback for top-level stamp quantity
        // ───────────────────────────────────────────────────────
        if (
          displayStampCost === 0 &&
          num(order.stampCertificateQtyPerSet) > 0
        ) {
          displayStampCost =
            num(order.stampCertificateQtyPerSet) *
            displayQty *
            num(order.stampCertificateCost);
        }
      }

      // ─────────────────────────────────────────────────────────
      // 4. Final Fine Weight
      // ─────────────────────────────────────────────────────────
      const displayFineWt = parseFloat(
        ((displayNetWt * totalWastage) / 100).toFixed(3),
      );

      // ─────────────────────────────────────────────────────────
      // 5. Total MC
      // ─────────────────────────────────────────────────────────
      const displayMc = parseFloat(
        (
          setMc * displayQty +
          gramMcPerGram * displayNetWt +
          specMcPerGram * displaySpecWt
        ).toFixed(2),
      );

      // ─────────────────────────────────────────────────────────
      // 6. Total Amount for this item
      //
      // MC + Stamp & Certificate
      // ─────────────────────────────────────────────────────────
      const displayTotalAmount = parseFloat(
        (displayMc + displayStampCost).toFixed(2),
      );

      return {
        ...order,

        displayGrossWt,
        displayNetWt,
        displaySpecWt,
        displayFineWt,

        displayMc,

        displayStampCost,

        displayTotalAmount,

        totalWastage,

        hasDetail,
        isApprox: !hasDetail,

        displayQty,
      };
    },
    [num],
  );

  // ─────────────────────────────────────────────────────────────
  // Group orders by seller + status
  // ─────────────────────────────────────────────────────────────
  const ordersBySellerAndStatus = useMemo(() => {
    const groupMap = new Map();

    orders.forEach((order) => {
      const sellerName = order.sellerId || order.operatorId || "Unknown Seller";

      const orderStatus = order.orderStatus || "Unknown Status";

      const groupKey = `${sellerName}||${orderStatus}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          sellerName,
          orderStatus,
          orders: [],

          totalItems: 0,
          totalFineWeight: 0,
          totalMc: 0,
          totalStampCost: 0,
          totalAmount: 0,
        });
      }

      const group = groupMap.get(groupKey);

      const enrichedOrder = enrichOrderWithMetrics(order);

      group.orders.push(enrichedOrder);

      group.totalItems += enrichedOrder.displayQty || 1;

      group.totalFineWeight += enrichedOrder.displayFineWt || 0;

      group.totalMc += enrichedOrder.displayMc || 0;

      group.totalStampCost += enrichedOrder.displayStampCost || 0;

      // MC + Stamp & Certificate
      group.totalAmount += enrichedOrder.displayTotalAmount || 0;
    });

    return Array.from(groupMap.values());
  }, [orders, enrichOrderWithMetrics]);

  // ─────────────────────────────────────────────────────────────
  // Fetch orders
  // ─────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    const buyerId = buyer?.buyerId;

    if (!buyerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "orderList"),
        where("buyerId", "==", buyerId),
        where("orderStatus", "in", config.statuses),
      );

      const snapshot = await getDocs(q);

      const fetched = snapshot.docs.map((doc) => ({
        ...doc.data(),

        docId: doc.id,
        _docId: doc.id,
        id: doc.id,
      }));

      setOrders(fetched);
      console.log(fetched);
    } catch (err) {
      setError(`Failed to load orders: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [buyer?.buyerId, config.statuses]);

  // ─────────────────────────────────────────────────────────────
  // Initial fetch
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ─────────────────────────────────────────────────────────────
  // Toggle seller
  // ─────────────────────────────────────────────────────────────
  const toggleSeller = useCallback((key) => {
    setExpandedSeller((cur) => (cur === key ? null : key));
  }, []);
  // ─────────────────────────────────────────────────────────────
  // Format date
  // ─────────────────────────────────────────────────────────────
  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return "N/A";

    const date =
      typeof dateValue === "object" && dateValue.seconds
        ? new Date(dateValue.seconds * 1000)
        : new Date(dateValue);

    return Number.isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        });
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Loading
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Header title={config.title} />

        <OrderListSkeleton count={3} />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Error
  // ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <Header title={config.title} />

        <div className="orders-container">
          <div className="error-state">
            <p className="error">{error}</p>

            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Header title={config.title} />

      <div className="orders-container">
        <div className="orders-header">
          <p>
            {orders.length} order
            {orders.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <p>No orders in progress found.</p>

            <p className="debug-info">
              Buyer ID: {buyer?.buyerId || "Not found"} | Statuses:{" "}
              {config.statuses.join(", ")}
            </p>
          </div>
        ) : (
          <div className="seller-orders-container">
            {ordersBySellerAndStatus.map((sellerGroup) => {
              const groupKey = `${sellerGroup.sellerName}||${sellerGroup.orderStatus}`;

              const isExpanded = expandedSeller === groupKey;

              const firstOrder = sellerGroup.orders[0];

              const enrichedOrders = sellerGroup.orders;

              // ─────────────────────────────────────────
              // Progress steps
              // ─────────────────────────────────────────
              const statusSteps = [
                {
                  key: "Assigned",
                  label: "Accepted",
                  icon: UserCheck,
                },
                {
                  key: "Picked Up",
                  label: "Picked Up",
                  icon: CheckCircle,
                },
                {
                  key: "InWarehouse",
                  label: "In Warehouse",
                  icon: PackageCheck,
                },
                {
                  key: "Dispatched",
                  label: "Dispatched",
                  icon: Truck,
                },
                {
                  key: "Delivered",
                  label: "Delivered",
                  icon: PackageCheck,
                },
              ];

              const TOTAL_STEPS = statusSteps.length;

              const statusToStep = {
                Accepted: 0,
                Assigned: 0,
                Assortment: 0,
                FinalCorrection: 0,
                RTD: 0,
                PickedUp: 1,
                InWarehouse: 2,
                Dispatched: 3,
                Delivered: 4,
              };

              const stepIdx = statusToStep[sellerGroup.orderStatus] ?? 0;

              const segW = 100 / TOTAL_STEPS;

              const trackSpan = segW * (TOTAL_STEPS - 1);

              const fillPct =
                trackSpan > 0
                  ? `${(stepIdx / (TOTAL_STEPS - 1)) * 100}%`
                  : "0%";

              // ─────────────────────────────────────────
              // Group totals
              // ─────────────────────────────────────────
              const displayFineWeight = parseFloat(
                sellerGroup.totalFineWeight.toFixed(3),
              );

              const displayTotalMc = parseFloat(sellerGroup.totalMc.toFixed(2));

              const displayTotalStampCost = parseFloat(
                sellerGroup.totalStampCost.toFixed(2),
              );

              const displayTotalAmount = parseFloat(
                (displayTotalMc + displayTotalStampCost).toFixed(2),
              );

              const hasAnyApprox = enrichedOrders.some((o) => o.isApprox);

              return (
                <div key={groupKey} className="seller-order-card">
                  {/* ───────────────────────────────────
                        Seller Header
                    ─────────────────────────────────── */}
                  <div
                    className="seller-header clickable"
                    onClick={() => toggleSeller(groupKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && toggleSeller(groupKey)
                    }
                  >
                    <div className="seller-info-row">
                      <div className="seller-meta">
                        <div className="order-meta-chips">
                          <span className="order-chip count-chip">
                            {sellerGroup.totalItems} item
                            {sellerGroup.totalItems !== 1 ? "s" : ""}
                          </span>

                          <span className="order-chip date-chip">
                            Placed {formatDate(firstOrder?.orderTime)}
                          </span>
                        </div>
                      </div>

                      <span className="accordbtn" aria-label="Toggle details">
                        {isExpanded ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </span>
                    </div>

                    {/* ───────────────────────────────
                          Summary Pills
                      ─────────────────────────────── */}
                    <div className="order-summary-pills">
                      <div className="summary-pill fine-pill">
                        <span className="pill-label">Total Fine Wt</span>

                        <span className="pill-value">
                          {hasAnyApprox ? "~" : ""}
                          {displayFineWeight.toFixed(3)}g
                        </span>
                      </div>

                      <div className="summary-pill mc-pill">
                        <span className="pill-label">Total Amount</span>

                        <span className="pill-value">
                          ₹{hasAnyApprox ? "~" : ""}
                          {displayTotalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ───────────────────────────────────
                        Progress Steps
                    ─────────────────────────────────── */}
                  <div className="progress-steps">
                    <div className="progress-track">
                      <div
                        className="progress-track-fill"
                        style={{
                          width: fillPct,
                        }}
                      />
                    </div>

                    {statusSteps.map((step, idx) => {
                      const Icon = step.icon;

                      const isActive = idx <= stepIdx;

                      const isCurrent = idx === stepIdx;

                      return (
                        <div
                          key={step.key}
                          className={`orderstep${isActive ? " active" : ""}${
                            isCurrent ? " current" : ""
                          }`}
                        >
                          <div className="step-icon">
                            <Icon size={18} />
                          </div>

                          <span>{step.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* ───────────────────────────────────
                        Expanded Order Items
                    ─────────────────────────────────── */}
                  {isExpanded && (
                    <>
                      <div className="divider" />

                      <div className="order-items">
                        {enrichedOrders.map((order) => {
                          const imageUrl = order.images?.[0]?.url?.trim();

                          const displayQty = order.displayQty;

                          const displayGrossWt = order.displayGrossWt;

                          const displayNetWt = order.displayNetWt;

                          const displaySpecWt = order.displaySpecWt;

                          const displayFineWt = order.displayFineWt;

                          const displayMc = order.displayMc;

                          // IMPORTANT:
                          // Use enriched displayStampCost
                          const stampCost = order.displayStampCost;

                          const totalItemAmount = order.displayTotalAmount;

                          return (
                            <div
                              key={order.id || order.docId}
                              className="order-item"
                            >
                              {/* ─────────────────────
                                      Item Top
                                  ───────────────────── */}
                              <div className="item-top-row">
                                <div className="item-image">
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={order.productName || "Product"}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="image-placeholder">
                                      No Image
                                    </div>
                                  )}
                                </div>

                                <div className="item-header-row">
                                  <div className="item-title-row">
                                    <h4 className="item-name">
                                      {order.productName || "Unnamed Product"}
                                    </h4>

                                    <span
                                      className={`item-status status-${
                                        order.orderStatus?.toLowerCase() ||
                                        "unknown"
                                      }`}
                                    >
                                      {order.orderStatus || "In Progress"}
                                    </span>
                                  </div>

                                  <div className="item-specs">
                                    <span className="spec-badge purity-badge">
                                      {order.category.toUpperCase()}
                                    </span>

                                    <span className="spec-badge">
                                      Qty: {displayQty}
                                    </span>

                                    <span className="spec-badge">
                                      {order.isApprox ? "~" : ""}
                                      Gross: {displayGrossWt.toFixed(3)}g
                                    </span>

                                    <span className="spec-badge">
                                      {order.isApprox ? "~" : ""}
                                      Net: {displayNetWt.toFixed(3)}g
                                    </span>

                                    {displaySpecWt > 0 && (
                                      <span className="spec-badge spec-wt-badge">
                                        {order.isApprox ? "~" : ""}
                                        Spec: {displaySpecWt.toFixed(3)}g
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* ─────────────────────
                                      Item Details
                                  ───────────────────── */}
                              <div className="item-details">
                                <div className="item-metrics-grid">
                                  {/* Gross */}
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Gross Wt
                                    </span>

                                    <strong className="metric-val">
                                      {order.isApprox ? "~" : ""}
                                      {displayGrossWt.toFixed(3)}g
                                    </strong>
                                  </div>

                                  {/* Net */}
                                  <div className="metric-cell">
                                    <span className="metric-label">Net Wt</span>

                                    <strong className="metric-val">
                                      {order.isApprox ? "~" : ""}
                                      {displayNetWt.toFixed(3)}g
                                    </strong>
                                  </div>
                                  {/* Fine */}
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Fine Wt
                                    </span>

                                    <strong className="metric-val fine-highlight">
                                      {order.isApprox ? "~" : ""}
                                      {displayFineWt.toFixed(3)}g
                                    </strong>
                                  </div>

                                  {/* MC */}
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Total MC
                                    </span>

                                    <strong className="metric-val mc-highlight">
                                      {order.isApprox ? "~" : ""}₹
                                      {displayMc.toFixed(2)}
                                    </strong>
                                  </div>

                                  {/* Stamp & Certificate */}
                                  {stampCost > 0 && (
                                    <div className="metric-cell stamp-cell">
                                      <span className="metric-label">
                                        Stamp & Cert
                                      </span>

                                      <strong className="metric-val stamp-highlight">
                                        ₹{stampCost.toFixed(2)}
                                      </strong>
                                    </div>
                                  )}

                                  {/* Item Total */}
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Total Amount
                                    </span>

                                    <strong className="metric-val">
                                      ₹{totalItemAmount.toFixed(2)}
                                    </strong>
                                  </div>
                                </div>

                                {/* ─────────────────────
                                        Size Wise Breakdown
                                    ───────────────────── */}
                                {order.orderDetail &&
                                  Object.keys(order.orderDetail).length > 0 && (
                                    <div className="od-order-detail-section">
                                      <h5 className="od-order-detail-title">
                                        Size Wise Breakdown
                                      </h5>

                                      <div className="od-order-detail-cards">
                                        {Object.entries(order.orderDetail).map(
                                          ([sizeName, detail]) => {
                                            const stampQty = num(
                                              detail.stampCertificateQtyPerSet,
                                            );

                                            const sizeFinalQty =
                                              detail.finalQty !== undefined
                                                ? num(detail.finalQty)
                                                : num(detail.qty) -
                                                  num(detail.returnQty);

                                            const sizeStampCost =
                                              stampQty *
                                              Math.max(0, sizeFinalQty) *
                                              num(order.stampCertificateCost);

                                            return (
                                              <div
                                                key={sizeName}
                                                className="od-size-detail-card"
                                              >
                                                <div className="od-size-detail-header">
                                                  <span className="od-size-name">
                                                    {sizeName}
                                                  </span>

                                                  <span className="od-size-qty">
                                                    Qty: {detail.qty}
                                                  </span>
                                                </div>

                                                <div className="od-size-detail-grid">
                                                  <span>
                                                    Gross:{" "}
                                                    <strong>
                                                      {num(
                                                        detail.grossWT,
                                                      ).toFixed(3)}
                                                      g
                                                    </strong>
                                                  </span>

                                                  <span>
                                                    Net:{" "}
                                                    <strong>
                                                      {num(
                                                        detail.netwt,
                                                      ).toFixed(3)}
                                                      g
                                                    </strong>
                                                  </span>

                                                  {num(detail.specWT) > 0 && (
                                                    <span>
                                                      Spec:{" "}
                                                      <strong>
                                                        {num(
                                                          detail.specWT,
                                                        ).toFixed(3)}
                                                        g
                                                      </strong>
                                                    </span>
                                                  )}

                                                  {stampQty > 0 && (
                                                    <span className="stamp-detail-tag">
                                                      Stamp:{" "}
                                                      <strong>
                                                        {stampQty}
                                                        /set
                                                      </strong>
                                                      {" · "}
                                                      <strong>
                                                        ₹
                                                        {sizeStampCost.toFixed(
                                                          2,
                                                        )}
                                                      </strong>
                                                    </span>
                                                  )}
                                                </div>

                                                {num(detail.returnQty) > 0 && (
                                                  <div className="od-size-return-info">
                                                    <div className="od-return-badge">
                                                      Return: {detail.returnQty}{" "}
                                                      pcs ·{" "}
                                                      {num(
                                                        detail.returnGrossWt,
                                                      ).toFixed(3)}
                                                      g gross ·{" "}
                                                      {num(
                                                        detail.returnNetWt,
                                                      ).toFixed(3)}
                                                      g net
                                                    </div>

                                                    {detail.returnReason && (
                                                      <div className="od-return-reason-text">
                                                        Reason:{" "}
                                                        <span>
                                                          {detail.returnReason}
                                                        </span>
                                                      </div>
                                                    )}

                                                    <div className="od-final-badge">
                                                      Final: {sizeFinalQty} pcs
                                                      ·{" "}
                                                      {num(
                                                        detail.finalGrossWt,
                                                      ).toFixed(3)}
                                                      g gross ·{" "}
                                                      {num(
                                                        detail.finalNetWt,
                                                      ).toFixed(3)}
                                                      g net
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default OrdersInProgress;
